import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { RootState } from "app/lib/store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/jointpersonalDetails";
import { usePersonalDetails } from "app/hooks/applicant/usePersonalDetails";

import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";

import InfoTitle from "app/components/common/typography/InfoTitle";

import { JointPersonalFormFiveData } from "app/utils/types";
import { useNavigate } from "react-router-dom";
import { APPLICANT } from "app/constants/apiEndPoints";
import toast from "react-hot-toast";
import DynamicFormFive from "app/components/Individual/personalDetails/dynamicForms/DynamicFormFive";

const PartFive: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { mutateAsync: submitPersonalDetails, isPending } =
    usePersonalDetails();
  const { formFive, ...others } = useSelector(
    (state: RootState) => state.jointpersonalDetails.stepsData
  );
  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );
  const applicant = useSelector(
    (state: RootState) => state.loanTypesSteps.jointApplication
  );

  const { loanApplication } = useSelector(
    (state: RootState) => state.loanTypesSteps.stepsData
  );

  const isSpouse = loanApplication?.coApplicantRelationship === "spouse";

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    setError,
    register,
    watch,
    clearErrors,
  } = useForm<JointPersonalFormFiveData>({
    defaultValues: {
      yourDetails: {
        sameMailingAddress: false,
      },
      coBorrowerDetails: {
        sameMailingAddress: false,
      },
    },
  });

  const onSubmit = async (data: JointPersonalFormFiveData) => {
    const { yourDetails, coBorrowerDetails } = data || {};
    const {
      sameMailingAddress,
      street,
      postCode,
      state,
      housingStatus,
      ...othersYourDetails
    } = yourDetails;

    const {
      sameMailingAddress: cosameMailingAddress,
      street: coMailingAddress,
      postCode: coPostCode,
      state: coState,
      housingStatus: coHousingStatus,
      ...otherscoBorrowerDetails
    } = coBorrowerDetails;

    const yourDetailsData = {
      ...othersYourDetails,
      yearsOfStay: Number(yourDetails.yearsOfStay),
      monthsOfStay: Number(yourDetails.monthsOfStay),
      sameMailingAddress,
      housingStatus,
      ...(cosameMailingAddress
        ? {}
        : { street, postCode: String(postCode), state }),
    };

    const coBorrowerDetailsData = {
      ...otherscoBorrowerDetails,
      yearsOfStay: Number(coBorrowerDetails.yearsOfStay),
      monthsOfStay: Number(coBorrowerDetails.monthsOfStay),
      sameMailingAddress: cosameMailingAddress,
      housingStatus: isSpouse ? housingStatus : coHousingStatus,
      ...(cosameMailingAddress
        ? {}
        : {
            street: coMailingAddress,
            postCode: String(postCode),
            state: coState,
          }),
    };

    const request1 = {
      url: `${APPLICANT}/${savedIds?.applicantID}/fifth`,
      data: yourDetailsData,
    };
    const request2 = {
      url: `${APPLICANT}/${savedIds?.secondApplicantID}/fifth`,
      data: coBorrowerDetailsData,
    };

    const savedYourDetailsData = {
      ...yourDetails,
      yearsOfStay: Number(yourDetails.yearsOfStay),
      monthsOfStay: Number(yourDetails.monthsOfStay),
    };
    const savedCoBorrowerDetailsData = {
      ...coBorrowerDetails,
      housingStatus: coHousingStatus,
      yearsOfStay: Number(coBorrowerDetails.yearsOfStay),
      monthsOfStay: Number(coBorrowerDetails.monthsOfStay),
    };

    const storedData = {
      yourDetails: savedYourDetailsData,
      coBorrowerDetails: savedCoBorrowerDetailsData,
    };

    try {
      const [response1, response2] = await Promise.all([
        submitPersonalDetails(request1),
        submitPersonalDetails(request2),
      ]);
      if (response1?.success && response2?.success) {
        navigate(`/joint-employment-details`);
        dispatch(
          addStepsData({
            ...others,
            formFive: storedData,
          })
        );
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something went wrong.");
    }
  };

  useEffect(() => {
    if (formFive) {
      reset(formFive);
    } else {
      const { YourDetails, Coborrower } = applicant || {};
      if (YourDetails?.address?.postCode) {
        setValue("yourDetails.postCode", YourDetails?.address?.postCode);
      }
      if (YourDetails?.address?.street) {
        setValue("yourDetails.street", YourDetails?.address?.street);
      }
      if (YourDetails?.address?.state) {
        setValue("yourDetails.state", YourDetails?.address?.state);
      }

      if (Coborrower?.address?.postCode) {
        setValue("coBorrowerDetails.postCode", Coborrower?.address?.postCode);
      }
      if (YourDetails?.address?.street) {
        setValue("coBorrowerDetails.street", Coborrower?.address?.street);
      }
      if (Coborrower?.address?.state) {
        setValue("coBorrowerDetails.state", Coborrower?.address?.state);
      }
    }
  }, [formFive, reset]);

  return (
    <>
      <ProgressBar progress={100} MB={4} />
      <div className="w-full sm:w-[60%] lg:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Personal Details" />
        {/* <SubTitle title="Please enter you loan and baloon amount" /> */}
      </div>
      <div className="mx-auto w-internalWidth">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex mb-5 flex-col md:flex-col xl:flex-row sm:gap-4 ">
            <div className="flex-1">
              <InfoTitle
                title="Your Details"
                mobileMarginBottom="sm:mb-4 !mb-6"
              />

              <DynamicFormFive
                control={control}
                errors={errors}
                register={register}
                setValue={setValue}
                setError={setError}
                clearErrors={clearErrors}
                sameMailingAddress="yourDetails.sameMailingAddress"
                residentialAddressValue={watch(
                  "yourDetails.sameMailingAddress"
                )}
                housingStatus="yourDetails.housingStatus"
                monthsOfStay="yourDetails.monthsOfStay"
                yearsOfStay="yourDetails.yearsOfStay"
                addressStreetName="yourDetails.street"
                addressStateName="yourDetails.state"
                addressPostCodeName="yourDetails.postCode"
              />
            </div>
            <div className="flex-1">
              <InfoTitle
                title="Co-borrower"
                mobileMarginBottom="sm:mb-4 !mb-6"
              />
              <DynamicFormFive
                control={control}
                errors={errors}
                register={register}
                setValue={setValue}
                setError={setError}
                clearErrors={clearErrors}
                sameMailingAddress="coBorrowerDetails.sameMailingAddress"
                residentialAddressValue={watch(
                  "coBorrowerDetails.sameMailingAddress"
                )}
                housingStatus="coBorrowerDetails.housingStatus"
                monthsOfStay="coBorrowerDetails.monthsOfStay"
                yearsOfStay="coBorrowerDetails.yearsOfStay"
                addressStreetName="coBorrowerDetails.street"
                addressStateName="coBorrowerDetails.state"
                addressPostCodeName="coBorrowerDetails.postCode"
                housingStatusDisabled={isSpouse}
              />
            </div>
          </div>

          <div className="flex justify-between mb-6">
            <PreviousButtom
              onClick={() => {
                dispatch(prevStep());
              }}
            >
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartFive;
